/**
 * EDM revision viewer — the 2D sheet and the 3D model, with mark-ups on both.
 *
 * The 2D half never waits on the 3D half. Where the derivative is queued, translating or
 * failed, the sheet, the comments and the review cycle are fully usable and the 3D tab
 * says what state it is actually in — in the service's own words where the 3D model service refused.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ArrowLeft, Box, FileText, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { useNfaTheme } from '@/components/nfa/nfaTheme';
import Edm2dSheet from '@/components/edm/viewer/Edm2dSheet';
import Edm3dModel from '@/components/edm/viewer/Edm3dModel';
import EdmMarkupPanel from '@/components/edm/viewer/EdmMarkupPanel';
import { STAMPS, TOOL_ICON_ORDER } from '@/components/edm/viewer/markupGeometry';

const SEVERITIES = ['HOLD', 'MAJOR', 'MINOR', 'QUERY', 'INFORMATION'];

const STATE_TONE = {
  ready: '#22C55E', queued: '#E2A93B', in_progress: '#2D86C7', uploading: '#2D86C7',
  failed: '#EF4444', timed_out: '#EF4444', not_submitted: '#94A3B8', absent: '#94A3B8',
};

export default function EdmRevisionViewer() {
  const t = useNfaTheme();
  const { revisionId } = useParams();
  const [state, setState] = useState(null);
  const [markups, setMarkups] = useState(null);
  const [gate, setGate] = useState(null);
  const [err, setErr] = useState(null);
  const [dim, setDim] = useState('2d');
  const [page, setPage] = useState(1);
  const [tool, setTool] = useState(null);
  const [severity, setSeverity] = useState('MINOR');
  const [body, setBody] = useState('');
  const [stamp, setStamp] = useState(STAMPS[0]);
  const [noteText, setNoteText] = useState('');
  const [visibleLayers, setVisibleLayers] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [retrying, setRetrying] = useState(false);

  const load = useCallback(() => {
    api.get(`/edm/revisions/${revisionId}/viewer`)
      .then((r) => setState(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || 'Could not open this revision'));
    api.get(`/edm/revisions/${revisionId}/markups`).then((r) => setMarkups(r.data)).catch(() => {});
    api.get(`/edm/revisions/${revisionId}/comments`)
      .then((r) => setGate(r.data?.closeOutGate)).catch(() => {});
  }, [revisionId]);
  useEffect(load, [load]);

  // Poll only while a translation is actually moving, and stop when it lands.
  useEffect(() => {
    const s = state?.threeD?.state;
    if (!['queued', 'in_progress', 'uploading'].includes(s)) return undefined;
    const h = setInterval(() => {
      api.get(`/edm/revisions/${revisionId}/viewer`).then((r) => setState(r.data)).catch(() => {});
    }, 6000);
    return () => clearInterval(h);
  }, [state?.threeD?.state, revisionId]);

  const commit = useCallback(async (entity, extra = {}) => {
    try {
      await api.post(`/edm/revisions/${revisionId}/markups`, {
        dimension: entity.dimension || '2d',
        entities: entity.entities || [entity],
        page: entity.dimension === '3d' ? undefined : page,
        pageSize: state?.twoD?.pageSize,
        file_id: entity.dimension === '3d' ? state?.threeD?.fileId : state?.twoD?.fileId,
        viewerState: entity.viewerState,
        modelUrn: entity.modelUrn,
        body,
        severity,
        ...extra,
      });
      setBody(''); setTool(null); setNoteText('');
      toast.success('Mark-up saved as geometry against this revision — the file is untouched.');
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.why || 'The mark-up was refused'));
    }
  }, [revisionId, page, body, severity, state, load]);

  const retry = useCallback(async () => {
    setRetrying(true);
    try {
      await api.post(`/edm/files/${state.threeD.fileId}/translation/retry`);
      toast.success('Re-submitted to the 3D model service.');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Retry failed');
    } finally { setRetrying(false); }
  }, [state, load]);

  const three = state?.threeD;
  const tools = useMemo(() => TOOL_ICON_ORDER, []);

  if (err) {
    return (
      <DashboardLayout>
        <div className="p-8 text-sm" style={{ color: t.muted }} data-testid="edm-viewer-error">{err}</div>
      </DashboardLayout>
    );
  }
  if (!state) {
    return (
      <DashboardLayout>
        <div className="flex items-center gap-2 p-8 text-sm" style={{ color: t.muted }}
          data-testid="edm-viewer-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Opening the revision…
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="edm-revision-viewer">
        <div className="flex items-center gap-3">
          <a href={`/engineering/documents/${state.revision.document_id}`}
            data-testid="edm-viewer-back"
            className="inline-flex items-center gap-1.5 text-xs" style={{ color: t.muted }}>
            <ArrowLeft className="w-3.5 h-3.5" /> Back to the document
          </a>
          <h1 className="text-lg font-semibold" style={{ color: t.body }} data-testid="edm-viewer-title">
            Rev {state.revision.revision_code}
            <span className="text-sm font-normal ml-2" style={{ color: t.muted }}>
              {state.revision.reason_for_issue || ''}
            </span>
          </h1>
        </div>

        <div className="flex items-center gap-2">
          <button onClick={() => setDim('2d')} data-testid="edm-viewer-tab-2d"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border ${
              dim === '2d' ? 'bg-white/5 border-white/25' : 'border-white/10'}`}>
            <FileText className="w-3.5 h-3.5" /> Sheet
          </button>
          <button onClick={() => setDim('3d')} data-testid="edm-viewer-tab-3d"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border ${
              dim === '3d' ? 'bg-white/5 border-white/25' : 'border-white/10'}`}>
            <Box className="w-3.5 h-3.5" /> Model
            <span className="w-2 h-2 rounded-full" style={{ background: STATE_TONE[three?.state] }} />
          </button>
          <span className="text-[11px]" style={{ color: t.muted }} data-testid="edm-viewer-3d-state">
            {three?.headline}
          </span>
        </div>

        <div className="flex items-center gap-2 flex-wrap rounded-xl border px-2.5 py-2"
          style={{ borderColor: t.border }} data-testid="edm-comment-bar">
          <span className="text-[11px]" style={{ color: t.muted }}>
            The comment this mark-up carries
          </span>
          <input value={body} onChange={(e) => setBody(e.target.value)}
            placeholder="e.g. support spacing exceeds the piping spec at grid C-7"
            data-testid="edm-markup-body"
            className="text-[12px] rounded px-2 py-1 bg-transparent border flex-1 min-w-[220px]"
            style={{ borderColor: t.border }} />
          <select value={severity} onChange={(e) => setSeverity(e.target.value)}
            data-testid="edm-markup-severity"
            className="text-[11px] rounded px-1.5 py-1 bg-transparent border"
            style={{ borderColor: t.border }}>
            {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-4">
          <div className="rounded-xl border overflow-hidden" style={{ borderColor: t.border }}>
            {dim === '2d' && (
              <>
                <div className="flex items-center gap-1.5 flex-wrap px-2 py-2 border-b"
                  style={{ borderColor: t.border }} data-testid="edm-markup-toolbar">
                  {tools.map((k) => (
                    <button key={k} onClick={() => setTool(tool === k ? null : k)}
                      data-testid={`edm-tool-${k}`}
                      className={`text-[11px] px-2 py-1 rounded border capitalize ${
                        tool === k ? 'bg-amber-500/15 border-amber-500/40 text-amber-300'
                          : 'border-white/10 hover:bg-white/5'}`}>
                      {k === 'cloud' ? 'revision cloud' : k}
                    </button>
                  ))}
                  {tool === 'stamp' && (
                    <select value={stamp} onChange={(e) => setStamp(e.target.value)}
                      data-testid="edm-markup-stamp-select"
                      className="text-[11px] rounded px-1.5 py-1 bg-transparent border"
                      style={{ borderColor: t.border }}>
                      {STAMPS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  )}
                  {tool === 'text' && (
                    <input value={noteText} onChange={(e) => setNoteText(e.target.value)}
                      placeholder="note text, then click the sheet"
                      data-testid="edm-markup-note-text"
                      className="text-[11px] rounded px-2 py-1 bg-transparent border w-56"
                      style={{ borderColor: t.border }} />
                  )}
                </div>
                {state.twoD.available ? (
                  <Edm2dSheet fileId={state.twoD.fileId} markups={markups?.markups}
                    page={page} setPage={setPage} tool={tool} colour="#E8622C"
                    visibleLayers={visibleLayers} onCommit={commit}
                    pendingText={noteText} pendingStamp={stamp}
                    selectedId={selectedId} onSelectMarkup={setSelectedId} />
                ) : (
                  <div className="p-8 text-sm" style={{ color: t.muted }} data-testid="edm-2d-unavailable">
                    {state.twoD.reason}
                  </div>
                )}
              </>
            )}

            {dim === '3d' && (
              three?.state === 'ready' && three?.urn ? (
                <Edm3dModel projectId={state.revision.project_id} urn={three.urn}
                  fileId={three.fileId} region={three.region} markups={markups?.markups}
                  visibleLayers={visibleLayers} selectedId={selectedId}
                  onSelectMarkup={setSelectedId} onCommit={commit} />
              ) : (
                <div className="p-8" data-testid="edm-3d-unavailable">
                  <div className="text-sm font-medium mb-1" style={{ color: t.body }}>
                    {three?.headline}
                  </div>
                  {three?.context && (
                    <div className="text-[12px] mb-2" style={{ color: t.muted }}>{three.context}</div>
                  )}
                  {three?.detail && (
                    <pre className="text-[11px] whitespace-pre-wrap rounded p-2.5 mb-3"
                      style={{ background: 'rgba(255,255,255,0.04)', color: t.muted }}
                      data-testid="edm-3d-aps-reason">{three.detail}</pre>
                  )}
                  {['queued', 'in_progress', 'uploading'].includes(three?.state) && (
                    <div className="text-[11px] mb-3" style={{ color: t.muted }}
                      data-testid="edm-3d-progress">
                      {three.progressPct}% · {three.queuedVsInProgress}
                    </div>
                  )}
                  {three?.canRetry && (
                    <button onClick={retry} disabled={retrying} data-testid="edm-3d-retry"
                      className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded font-medium"
                      style={{ background: '#E8622C', color: '#fff' }}>
                      {retrying ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        : <RefreshCw className="w-3.5 h-3.5" />} Retry the translation
                    </button>
                  )}
                  <div className="text-[11px] mt-3" style={{ color: t.muted }}>
                    The sheet, the comments and the review cycle are unaffected — switch to
                    Sheet and carry on.
                  </div>
                </div>
              )
            )}
          </div>

          <EdmMarkupPanel data={markups} visibleLayers={visibleLayers}
            setVisibleLayers={setVisibleLayers} selectedId={selectedId}
            onSelect={setSelectedId} onChanged={load} gate={gate} />
        </div>

        <div className="text-[11px] rounded-xl border px-3 py-2" data-testid="edm-viewer-provenance"
          style={{ borderColor: t.border, color: t.muted }}>
          Sheet: {state.twoD.filename || '—'} · SHA-256 {(state.twoD.sha256 || '').slice(0, 16)}…
          · {state.twoD.provenance || 'no PDF filed'}. {state.twoD.immutability}
        </div>
      </div>
    </DashboardLayout>
  );
}
